import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtDA, fmtDate, todayISO, STATUT_FACTURE, MODES_LABEL } from "@/lib/format";
import { downloadPdf, openFile, downloadExcel } from "@/lib/files";
import { FileUpload } from "@/components/FileUpload";
import { Plus, FileBarChart, Loader2, Wallet, FileDown, Paperclip, Upload, Pencil } from "lucide-react";
import { toast } from "sonner";

const BANK_MODES = ["banque", "virement", "cheque", "carte"];

export default function FacturesFournisseurs() {
  const { societes, societeParam, societeId, exercice } = useApp();
  const { can, role } = useAuth();
  const [items, setItems] = useState([]);
  const [totalReste, setTotalReste] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statut, setStatut] = useState("all");
  const [fournisseurs, setFournisseurs] = useState([]);
  const [comptes, setComptes] = useState([]);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/factures-fournisseurs", { params: { ...societeParam, statut: statut === "all" ? undefined : statut } });
      setItems(data.items);
      setTotalReste(data.total_reste);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [societeId, statut]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const [editId, setEditId] = useState(null);

  const openNew = () => {
    const sid = societeId !== "all" ? societeId : (societes[0]?.id || "");
    setEditId(null);
    setForm({ societe_id: sid, fournisseur_id: "", numero_facture: "", date_facture: todayISO(), montant_ht: "", tva: "", ttc: "", echeance: todayISO(), mode_paiement: "especes", observations: "", piece: null });
    loadFournisseurs(sid);
    setOpen(true);
  };

  const openEdit = (f) => {
    setEditId(f.id);
    setForm({ societe_id: f.societe_id, fournisseur_id: f.fournisseur_id, numero_facture: f.numero_facture || "", date_facture: f.date_facture,
      montant_ht: f.montant_ht ?? "", tva: f.tva ?? "", ttc: f.ttc ?? "", echeance: f.echeance || "", mode_paiement: f.mode_paiement || "especes",
      observations: f.observations || "", piece: f.piece_id ? { id: f.piece_id, filename: f.piece_nom } : null });
    loadFournisseurs(f.societe_id);
    setOpen(true);
  };

  const loadFournisseurs = (sid) => {
    if (!sid) return setFournisseurs([]);
    api.get("/fournisseurs", { params: { societe_id: sid } }).then((r) => setFournisseurs(r.data));
  };
  const loadComptes = (sid) => {
    if (!sid) return setComptes([]);
    api.get("/banque/comptes", { params: { societe_id: sid } }).then((r) => setComptes(r.data));
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.societe_id || !form.fournisseur_id) return toast.error("Société et fournisseur requis.");
    if (!form.numero_facture) return toast.error("Numéro de facture requis.");
    if (!form.ttc || Number(form.ttc) <= 0) return toast.error("Montant TTC invalide.");
    setSaving(true);
    try {
      const payload = {
        ...form,
        echeance: form.echeance || null,
        montant_ht: Number(form.montant_ht) || 0,
        tva: Number(form.tva) || 0,
        ttc: Number(form.ttc),
        piece_id: form.piece?.id || null,
        piece_nom: form.piece?.filename || null,
      };
      if (editId) await api.put(`/factures-fournisseurs/${editId}`, payload);
      else await api.post("/factures-fournisseurs", payload);
      toast.success(editId ? "Facture modifiée." : "Facture créée.");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const openPay = (f) => {
    setPayFor(f);
    setPay({ date: todayISO(), montant: f.reste, mode: "banque", compte_id: "", reference: "" });
    loadComptes(f.societe_id);
  };

  const submitPay = async () => {
    if (!pay.montant || Number(pay.montant) <= 0) return toast.error("Montant invalide.");
    try {
      await api.post(`/factures-fournisseurs/${payFor.id}/paiements`, {
        ...pay,
        montant: Number(pay.montant),
        compte_id: BANK_MODES.includes(pay.mode) ? pay.compte_id || null : null,
      });
      toast.success("Paiement enregistré.");
      setPayFor(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Factures fournisseurs"
        subtitle="Dettes fournisseurs et paiements"
        testid="factures-page"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { const sid = societeId !== "all" ? societeId : (societes[0]?.id || ""); if (!sid) return toast.error("Sélectionnez une société."); downloadPdf(`/pdf/journal-achats?societe_id=${sid}&annee=${exercice}`, `journal-achats-${exercice}`); }} className="gap-2" data-testid="journal-achats-btn"><FileBarChart className="h-4 w-4" /> Journal des achats (PDF)</Button>
            <Button variant="outline" onClick={() => downloadExcel(`/export/achats${societeId !== "all" ? `?societe_id=${societeId}` : ""}`, "export-achats")} className="gap-2" data-testid="export-achats-btn"><FileDown className="h-4 w-4" /> Exporter (Excel)</Button>
            {can("factures_fournisseurs") && <Button onClick={openNew} className="gap-2" data-testid="new-facture-btn"><Plus className="h-4 w-4" /> Nouvelle facture</Button>}
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 kpi-grid">
        <KpiCard testid="fact-reste" label="Total reste à payer" value={totalReste} icon={FileBarChart} tone="warning" />
        <KpiCard testid="fact-count" label="Nombre de factures" value={items.length} isCurrency={false} icon={FileBarChart} />
        <KpiCard testid="fact-ttc" label="Total TTC" value={items.reduce((s, f) => s + (f.statut !== "annule" ? f.ttc : 0), 0)} icon={FileBarChart} />
      </div>

      <div className="mb-4 w-full sm:w-56">
        <Select value={statut} onValueChange={setStatut}>
          <SelectTrigger data-testid="statut-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="a_payer">À payer</SelectItem>
            <SelectItem value="partiellement_payee">Partiellement payée</SelectItem>
            <SelectItem value="payee">Payée</SelectItem>
            <SelectItem value="en_retard">En retard</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <EmptyState icon={FileBarChart} title="Aucune facture" hint="Enregistrez les factures reçues de vos fournisseurs." />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="factures-table">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>N° Facture</TableHead>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Échéance</TableHead>
                  <TableHead className="text-right">TTC</TableHead>
                  <TableHead className="text-right">Reste</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((f) => {
                  const st = STATUT_FACTURE[f.statut] || {};
                  return (
                    <TableRow key={f.id} data-testid={`fact-row-${f.id}`}>
                      <TableCell className="tabular text-sm font-medium">{f.numero_facture}</TableCell>
                      <TableCell className="text-sm">{f.fournisseur_name}</TableCell>
                      <TableCell className="tabular text-sm">{fmtDate(f.date_facture)}</TableCell>
                      <TableCell className="tabular text-sm">{fmtDate(f.echeance)}</TableCell>
                      <TableCell className="tabular text-right">{fmtDA(f.ttc)}</TableCell>
                      <TableCell className="tabular text-right font-semibold">{fmtDA(f.reste)}</TableCell>
                      <TableCell><span className={`text-xs px-2 py-1 rounded-md whitespace-nowrap ${st.cls}`}>{st.label}</span></TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          {f.piece_id && <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => openFile(f.piece_id)} title={f.piece_nom || "Justificatif"} data-testid={`fact-piece-${f.id}`}><Paperclip className="h-4 w-4" /></Button>}
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadPdf(`/pdf/facture-fournisseur/${f.id}`, f.numero_facture || f.numero)} data-testid={`fact-pdf-${f.id}`}><FileDown className="h-4 w-4" /></Button>
                          {role === "administrateur" && f.statut !== "annule" && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(f)} data-testid={`fact-edit-${f.id}`}><Pencil className="h-4 w-4" /></Button>}
                          {f.statut !== "payee" && f.statut !== "annule" && (
                            <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => openPay(f)} data-testid={`pay-btn-${f.id}`}>
                              <Wallet className="h-3.5 w-3.5" /> Payer
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create facture */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-xl max-h-[92vh] overflow-y-auto" data-testid="facture-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? "Modifier la facture fournisseur" : "Nouvelle facture fournisseur"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Société *</Label>
                <Select value={form.societe_id} disabled={!!editId} onValueChange={(v) => { setField("societe_id", v); loadFournisseurs(v); }}>
                  <SelectTrigger data-testid="fact-societe"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-popover">{societes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Fournisseur *</Label>
                <Select value={form.fournisseur_id} onValueChange={(v) => setField("fournisseur_id", v)}>
                  <SelectTrigger data-testid="fact-fournisseur"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-popover max-h-64">
                    {fournisseurs.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Aucun fournisseur</div>}
                    {fournisseurs.map((fo) => <SelectItem key={fo.id} value={fo.id}>{fo.raison_sociale}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>N° Facture *</Label><Input data-testid="fact-numero" value={form.numero_facture} onChange={(e) => setField("numero_facture", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Date facture</Label><DatePicker value={form.date_facture} onChange={(v) => setField("date_facture", v)} testid="fact-date" /></div>
              <div className="space-y-1.5"><Label>Montant HT</Label><Input type="number" step="0.01" value={form.montant_ht} onChange={(e) => setField("montant_ht", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>TVA</Label><Input type="number" step="0.01" value={form.tva} onChange={(e) => setField("tva", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Montant TTC *</Label><Input data-testid="fact-ttc" type="number" step="0.01" value={form.ttc} onChange={(e) => setField("ttc", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Échéance</Label><DatePicker value={form.echeance} onChange={(v) => setField("echeance", v)} testid="fact-echeance" /></div>
              <div className="space-y-1.5"><Label>Mode de paiement</Label>
                <Select value={form.mode_paiement} onValueChange={(v) => setField("mode_paiement", v)}>
                  <SelectTrigger data-testid="fact-mode-paiement"><SelectValue placeholder="Mode" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {["especes", "cheque", "virement"].map((m) => <SelectItem key={m} value={m}>{MODES_LABEL[m]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Facture (PDF / photo)</Label><FileUpload value={form.piece} onChange={(v) => setField("piece", v)} testid="fact-piece" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} data-testid="fact-submit" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment */}
      <Dialog open={!!payFor} onOpenChange={(v) => !v && setPayFor(null)}>
        <DialogContent className="bg-popover max-w-md" data-testid="paiement-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Enregistrer un paiement</DialogTitle></DialogHeader>
          {payFor && pay && (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted/50 p-3 text-sm flex justify-between">
                <span>{payFor.numero_facture} · {payFor.fournisseur_name}</span>
                <span className="tabular font-semibold">Reste {fmtDA(payFor.reste)}</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Date</Label><DatePicker value={pay.date} onChange={(v) => setPay((p) => ({ ...p, date: v }))} testid="pay-date" /></div>
                <div className="space-y-1.5"><Label>Montant *</Label><Input data-testid="pay-montant" type="number" step="0.01" value={pay.montant} onChange={(e) => setPay((p) => ({ ...p, montant: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label>Mode</Label>
                  <Select value={pay.mode} onValueChange={(v) => setPay((p) => ({ ...p, mode: v }))}>
                    <SelectTrigger data-testid="pay-mode"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      {["especes", "banque", "virement", "cheque", "carte"].map((m) => <SelectItem key={m} value={m}>{MODES_LABEL[m]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {BANK_MODES.includes(pay.mode) && (
                  <div className="space-y-1.5"><Label>Compte</Label>
                    <Select value={pay.compte_id} onValueChange={(v) => setPay((p) => ({ ...p, compte_id: v }))}>
                      <SelectTrigger data-testid="pay-compte"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent className="bg-popover">{comptes.map((c) => <SelectItem key={c.id} value={c.id}>{c.banque} · {c.nom_compte}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5 col-span-2"><Label>Référence</Label><Input value={pay.reference} onChange={(e) => setPay((p) => ({ ...p, reference: e.target.value }))} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>Annuler</Button>
            <Button onClick={submitPay} data-testid="pay-submit">Valider le paiement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
